import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SOPUploadDialog from "@/components/dialogs/SOPUploadDialog";
import api, { fileUrl, downloadUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Eye, Download, Upload, Trash2, CheckCircle2, Users, History } from "lucide-react";

export default function SOPLibrary() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sops, setSops] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [complianceSopId, setComplianceSopId] = useState(null);
  const [compliance, setCompliance] = useState([]);

  const load = async () => {
    const s = await api.get("/sops").catch(() => ({ data: [] }));
    setSops(s.data);
    if (isAdmin) {
      const e = await api.get("/employees").catch(() => ({ data: [] }));
      setEmployees(e.data);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const acknowledge = async (sopId) => {
    try {
      await api.post(`/sops/${sopId}/acknowledge`);
      toast.success("Marked as read & acknowledged");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const uploadNewVersion = async (sopId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/sops/${sopId}/new-version`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("New version uploaded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteSop = async (sopId) => {
    try {
      await api.delete(`/sops/${sopId}`);
      toast.success("SOP deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const viewCompliance = async (sopId) => {
    setComplianceSopId(sopId);
    const r = await api.get(`/sops/${sopId}/compliance`).catch(() => ({ data: [] }));
    setCompliance(r.data);
  };

  return (
    <DashboardLayout title="SOP Library">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-[#4B5563]">Version-controlled standard operating procedures with role-based visibility and read acknowledgment tracking.</p>
        {isAdmin && (
          <Button data-testid="add-sop-button" onClick={() => setUploadOpen(true)} className="bg-[#76C121] hover:bg-[#5C9819] text-white shrink-0 ml-4">
            <Plus className="h-4 w-4 mr-1.5" /> Publish SOP
          </Button>
        )}
      </div>

      <div className="space-y-3" data-testid="sop-list">
        {sops.length === 0 && <p className="text-sm text-[#4B5563] text-center py-10">No SOPs published yet.</p>}
        {sops.map((s) => (
          <div key={s.id} data-testid={`sop-card-${s.id}`} className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium uppercase tracking-wide text-[#00823B] bg-[#76C121]/10 px-2 py-0.5 rounded-md">{s.category}</span>
                <span className="text-xs text-[#4B5563]">v{s.version}</span>
                {!isAdmin && (
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-md ${s.acknowledged ? "bg-[#76C121]/10 text-[#00823B]" : "bg-amber-50 text-amber-700"}`}>
                    {s.acknowledged ? "Acknowledged" : "Pending Acknowledgment"}
                  </span>
                )}
              </div>
              <p className="text-sm font-semibold text-[#1C1C1E]">{s.title}</p>
              <p className="text-xs text-[#4B5563] mt-1">{s.description}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a href={fileUrl(s.file_path)} target="_blank" rel="noreferrer" data-testid={`sop-view-${s.id}`}>
                <Button size="sm" variant="outline"><Eye className="h-3.5 w-3.5 mr-1" /> View</Button>
              </a>
              <a href={downloadUrl(s.file_path, s.title)} data-testid={`sop-download-${s.id}`}>
                <Button size="sm" variant="outline"><Download className="h-3.5 w-3.5 mr-1" /> Download</Button>
              </a>
              {!isAdmin && !s.acknowledged && (
                <Button size="sm" data-testid={`sop-acknowledge-${s.id}`} onClick={() => acknowledge(s.id)} className="bg-[#00823B] hover:bg-[#006A2F] text-white">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark as Read
                </Button>
              )}
              {isAdmin && (
                <>
                  <Button size="sm" variant="outline" data-testid={`sop-compliance-${s.id}`} onClick={() => viewCompliance(s.id)}>
                    <Users className="h-3.5 w-3.5 mr-1" /> Compliance
                  </Button>
                  <label className="inline-block">
                    <Button size="sm" variant="outline" asChild data-testid={`sop-new-version-${s.id}`} className="cursor-pointer">
                      <span><Upload className="h-3.5 w-3.5 mr-1" /> New Version</span>
                    </Button>
                    <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadNewVersion(s.id, e.target.files[0])} />
                  </label>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`sop-delete-${s.id}`} className="h-8 w-8 flex items-center justify-center rounded-md border border-[#E2E8F0] text-red-600 hover:bg-red-50">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete "{s.title}"?</AlertDialogTitle>
                        <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteSop(s.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <SOPUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} employees={employees} onSaved={load} />

      <Dialog open={!!complianceSopId} onOpenChange={(v) => !v && setComplianceSopId(null)}>
        <DialogContent data-testid="sop-compliance-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><History className="h-4 w-4" /> Acknowledgment Compliance</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {compliance.length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No employees in scope for this SOP.</p>}
            {compliance.map((c) => (
              <div key={c.employee_id} data-testid={`compliance-row-${c.employee_id}`} className="flex items-center justify-between p-2 border border-[#E2E8F0] rounded-md text-sm">
                <span>{c.employee_name}</span>
                <span className={c.acknowledged ? "text-[#00823B] font-medium" : "text-amber-600 font-medium"}>
                  {c.acknowledged ? "Acknowledged" : "Pending"}
                </span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
